import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  useOutsideClick
} from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import { useToolbarPosition } from "@pathwright/ux/src/components/augmentation/AugmentationPositionedToolbar"
import { useEffect, useRef, useState } from "react"
import { useIntersection } from "react-use"
import { usePathCreatorResourcesQuery } from "../../api/generated"
import { useGetNewResource } from "../../path/create/CreateOrSelectResource"
import { usePathCreatorContext } from "../../path/create/PathCreatorContext"
import PathCreatorFlow from "../../path/create/PathCreatorFlow"
import { OnComplete } from "../../path/create/PathCreatorForm"
import Pathicon from "../../pathicon/Pathicon"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

type AddPathButtonProps = {
  onComplete?: OnComplete
  // Whether the PathCreatorForm inputs should auto show their related tooltip when
  // user focuses the input.
  autoShowTips?: boolean
}

// 3 flow states: "hidden", "animating", "active".
// "hidden" - flow is not visible, "Add Path" button is visible.
// "animating" - "Add Path" button is scrolled into view
// "active" - flow is visible, "Add Path" button is hidden.

const AddPathButton = ({ onComplete, autoShowTips }: AddPathButtonProps) => {
  const { me } = usePathwrightContext()
  const { t } = useTranslate()
  // TODO: use setInitialValue to reset after flow abandoned or completed.
  const { initialValue, setInitialValue } = usePathCreatorContext()
  const [flowState, setFlowState] = useState<"hidden" | "animating" | "active">(
    "hidden"
  )

  const { getNewResource, loading: loadingNewResource } = useGetNewResource()

  // Hide the button when toolbar is positioned at the bottom of the screen.
  // The toolbar actually renders an alternate add path button in this case,
  // which, when clicked, will trigger the flow in this component.
  const showButton = useToolbarPosition(
    {
      bottom: false,
      side: true
    },
    true /* allowOverlay */
  )
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const flowRef = useRef<HTMLDivElement>(null)
  const intersectionRef = useRef<HTMLDivElement>(null)
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: "0px",
    threshold: 1
  })

  const resourcesQuery = usePathCreatorResourcesQuery({
    skip: flowState === "hidden",
    onCompleted: async (data) => {
      // If there are no resources, we set the initial value so as to select the "create" option.
      const hasResources = (data?.resources?.edges?.length || 0) > 0
      if (!hasResources && !initialValue) {
        setInitialValue(await getNewResource())
      }
    }
  })

  // Track if a react-select listbox is present in the DOM.
  // Use this to determine if we should enable the click outside handler
  // for enabling the discard prompt.
  const [hasSelectListbox, setHasSelectListbox] = useState(false)

  useEffect(() => {
    if (flowState === "active") {
      const checkListbox = () => {
        setHasSelectListbox(
          !!flowRef.current?.querySelector(
            "[id^='react-select-'][id$='-listbox']"
          )
        )
      }

      const observer = new MutationObserver(checkListbox)
      if (flowRef.current) {
        observer.observe(flowRef.current, {
          childList: true,
          subtree: true
        })
      }

      return () => {
        observer.disconnect()
        setHasSelectListbox(false)
      }
    }
  }, [flowState])

  // If user clicks outside then closes out the flow, prompt to discard.
  useOutsideClick({
    ref: flowRef,
    handler: () => setConfirmDiscard(true),
    enabled: flowState === "active" && !hasSelectListbox
  })

  useEffect(() => {
    if (flowState === "animating") {
      if (intersection?.isIntersecting) {
        // Once flow comes into view, apply the deferred flow props.
        setFlowState("active")
      } else {
        // When we have deferred flow props, we scroll the flow into view.
        intersectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        })
      }
    }
  }, [flowState])

  // Activate the flow when animating and within view port.
  useEffect(() => {
    if (intersection?.isIntersecting && flowState === "animating") {
      setFlowState("active")
    }
  }, [intersection?.isIntersecting])

  // Animate the flow into view when initialValue is set.
  useEffect(() => {
    if (initialValue) {
      setFlowState("animating")
    }
  }, [initialValue])

  return (
    <>
      {flowState === "active" &&
      !resourcesQuery.loading &&
      !loadingNewResource ? (
        <Box pos="relative" w="100%" ref={flowRef}>
          <PathCreatorFlow
            targetUserId={me.id!}
            initialValue={initialValue}
            onComplete={async (data) => {
              await onComplete?.(data)
              setFlowState("hidden")
              setInitialValue()
            }}
            autoShowTips={autoShowTips}
          />
          {confirmDiscard && (
            <VStack
              justify="center"
              pos="absolute"
              inset={0}
              bg="blackAlpha.600"
              h="100%"
              borderRadius="xl"
              backdropFilter="blur(20px)"
              zIndex={100}
              spacing={4}
            >
              <Text color="white" size="xs" m={0}>
                Cancel adding this Path?
              </Text>
              {/* VStack in wireframes, HStack in real world (since less room in initial Path create/select step
              though click outside should just automatically discard in that case, but we didn't hoist state, so we're in the dark.) */}
              <HStack>
                <Button
                  size="sm"
                  onClick={() => {
                    setFlowState("hidden")
                    setConfirmDiscard(false)
                    setInitialValue()
                  }}
                  autoFocus
                >
                  Yes, cancel
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  onClick={() => setConfirmDiscard(false)}
                >
                  No, go back
                </Button>
              </HStack>
            </VStack>
          )}
        </Box>
      ) : showButton ? (
        <Button
          onClick={() => setFlowState("animating")}
          leftIcon={<Pathicon icon="plus" />}
          cursor="pointer"
          w="auto"
          m="0 auto"
          variant="brandReversed"
          shadow="rgba(0, 0, 0, .2) 0 0 10px 5px"
          backdropFilter="blur(20px)"
          borderRadius="50px"
          position="sticky"
          bottom={3}
          zIndex={10011}
          isLoading={resourcesQuery.loading || loadingNewResource}
        >
          {t("Add Path")}
        </Button>
      ) : null}

      {/* Render an element to scroll into view. Seems necessary for smooth scrolling. */}
      <Box
        ref={intersectionRef}
        height="1px"
        w="100%"
        // TODO: ensure we include enough height for the select menus
        // (calculate in some way)?
        // NOTE: only including margin bottom when form is active, mainly
        // to avoid extra space when registrations list hasn't finished loading.
        mb={flowState !== "hidden" ? "400px" : 0}
      />
    </>
  )
}

export default AddPathButton
